import React from 'react';
import { FaTicketAlt, FaUser, FaClock, FaCheckCircle, FaExclamationTriangle, FaEye, FaTag, FaProjectDiagram, FaHeadset, FaApple, FaAndroid, FaGlobe } from 'react-icons/fa';
import { getBaseUrl } from '../utils/tokenUtils';

const SupportTicketList = ({ supportTickets, onView }) => {
  const getStatusColor = (status) => {
    const colors = {
      open: 'text-red-400 bg-red-900/20 border-red-500/30',
      in_progress: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
      waiting_for_customer: 'text-blue-400 bg-blue-900/20 border-blue-500/30',
      resolved: 'text-green-400 bg-green-900/20 border-green-500/30',
      closed: 'text-gray-400 bg-gray-900/20 border-gray-500/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-900/20 border-gray-500/30';
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Open',
      in_progress: 'In Progress',
      waiting_for_customer: 'Waiting for Customer',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return texts[status] || status;
  };

  const renderStatusBadge = (status) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${getStatusColor(status)}`}>
      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current" />
      {getStatusText(status)}
    </span>
  );

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-400',
      medium: 'text-yellow-400',
      high: 'text-orange-400',
      urgent: 'text-red-400'
    };
    return colors[priority] || 'text-gray-400';
  };

  const getPriorityText = (priority) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getPriorityIcon = (priority, status) => {
    const isResolvedOrClosed = status === 'resolved' || status === 'closed';
    const iconSize = "h-4 w-4";
    const grayColor = "text-gray-400";
    const redColor = "text-red-400";
    
    if (isResolvedOrClosed) {
      if (priority === 'urgent') return <FaExclamationTriangle className={`${iconSize} ${grayColor}`} />;
      if (priority === 'high') return <FaExclamationTriangle className={`${iconSize} ${grayColor}`} />;
      if (priority === 'medium') return <FaTicketAlt className={`${iconSize} ${grayColor}`} />;
      return <FaTicketAlt className={`${iconSize} ${grayColor}`} />;
    }
    
    // For non-resolved/closed tickets, make all icons red
    if (priority === 'urgent') return <FaExclamationTriangle className={`${iconSize} ${redColor}`} />;
    if (priority === 'high') return <FaExclamationTriangle className={`${iconSize} ${redColor}`} />;
    if (priority === 'medium') return <FaTicketAlt className={`${iconSize} ${redColor}`} />;
    return <FaTicketAlt className={`${iconSize} ${redColor}`} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };


  if (!supportTickets || supportTickets.length === 0) {
    return (
      <div className="text-center py-12">
        <FaTicketAlt className="h-16 w-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-300 mb-2">No Support Tickets</h3>
        <p className="text-gray-400">Support tickets will appear here when customers contact you through your support modules via API.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {supportTickets.map((supportTicket) => (
        <div key={supportTicket._id} className="w-full min-w-0 overflow-hidden bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-all duration-200">
          {/* Mobile-First Responsive Layout */}
          <div className="p-3 sm:p-4">
            {/* Mobile Layout */}
            <div className="block sm:hidden">
              <div className="flex items-start justify-between mb-3">
                {/* Priority Icon and Project Info */}
                <div className="flex items-center space-x-2">
                  <div 
                    className="flex items-center"
                    title={`Priority: ${getPriorityText(supportTicket.priority)}`}
                  >
                    {getPriorityIcon(supportTicket.priority, supportTicket.status)}
                  </div>
                  
                  <div className="relative">
                    {supportTicket.projectId?.logo ? (
                      <img 
                        src={supportTicket.projectId.logo.startsWith('http') ? supportTicket.projectId.logo : `${getBaseUrl()}${supportTicket.projectId.logo}`}
                        alt={supportTicket.projectId.name}
                        className="w-6 h-6 rounded object-cover border border-gray-600"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center" style={{ display: supportTicket.projectId?.logo ? 'none' : 'flex' }}>
                      <FaProjectDiagram className="w-3 h-3 text-gray-400" />
                    </div>
                    
                    {/* Platform Badge */}
                    {supportTicket.productId?.platform && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-800 rounded-full border border-gray-600 flex items-center justify-center">
                        {supportTicket.productId.platform === 'ios' ? (
                          <FaApple className="w-1.5 h-1.5 text-gray-300" />
                        ) : supportTicket.productId.platform === 'android' ? (
                          <FaAndroid className="w-1.5 h-1.5 text-green-400" />
                        ) : (
                          <FaGlobe className="w-1.5 h-1.5 text-blue-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* View Button - Mobile */}
                <button
                  onClick={() => onView(supportTicket)}
                  className="flex items-center px-2 py-1 bg-gray-800 text-white rounded text-xs hover:bg-gray-700 transition-colors border border-gray-700"
                  title="View Details"
                >
                  <FaEye className="mr-1" />
                  View
                </button>
              </div>
              
              {/* Subject and Description - Mobile */}
                <div className="mb-2">
                  <h3 className="min-w-0 break-words text-sm font-semibold text-white mb-1">
                    {supportTicket.subject}
                  </h3>
                  <div className="mb-1">{renderStatusBadge(supportTicket.status)}</div>
                <p className="min-w-0 break-words text-xs text-gray-300 mb-1">
                  {supportTicket.description}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{supportTicket.projectId?.name || 'Unknown Project'}</span>
                  <span>
                    {getTimeAgo(
                      supportTicket.timestamps?.updatedAt ||
                      supportTicket.timestamps?.lastResponseAt ||
                      supportTicket.timestamps?.createdAt
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:grid min-w-0 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
              {/* Left Side - Main Info */}
              <div className="flex min-w-0 items-center space-x-3 overflow-hidden">
                {/* Ticket Info - Compact */}
                <div className="flex flex-col space-y-0.5 flex-shrink-0">
                  {/* Ticket Number with Priority Icon */}
                  <div className="flex items-center space-x-1">
                    <div 
                      className="flex items-center"
                      title={`Priority: ${getPriorityText(supportTicket.priority)}`}
                    >
                      {getPriorityIcon(supportTicket.priority, supportTicket.status)}
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <div className="relative">
                    {supportTicket.projectId?.logo ? (
                      <img 
                        src={supportTicket.projectId.logo.startsWith('http') ? supportTicket.projectId.logo : `${getBaseUrl()}${supportTicket.projectId.logo}`}
                        alt={supportTicket.projectId.name}
                        className="w-8 h-8 rounded object-cover border border-gray-600"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center" style={{ display: supportTicket.projectId?.logo ? 'none' : 'flex' }}>
                      <FaProjectDiagram className="w-4 h-4 text-gray-400" />
                    </div>
                    
                    {/* Platform Badge - Top Right */}
                    {supportTicket.productId?.platform && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-800 rounded-full border border-gray-600 flex items-center justify-center">
                        {supportTicket.productId.platform === 'ios' ? (
                          <FaApple className="w-2 h-2 text-gray-300" />
                        ) : supportTicket.productId.platform === 'android' ? (
                          <FaAndroid className="w-2 h-2 text-green-400" />
                        ) : (
                          <FaGlobe className="w-2 h-2 text-blue-400" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="min-w-0 flex flex-col">
                    <span className="block max-w-20 truncate text-xs text-gray-400">
                      {supportTicket.projectId?.name || 'Unknown Project'}
                    </span>
                  </div>
                </div>

                {/* Subject and Description */}
                <div className="min-w-0 flex-1 overflow-hidden text-left">
                  <h3 className="truncate break-words text-sm font-semibold text-white">
                    {supportTicket.subject}
                  </h3>
                  <div className="mt-1">{renderStatusBadge(supportTicket.status)}</div>
                  <p className="truncate break-words text-xs text-gray-300">
                    {supportTicket.description}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {getTimeAgo(
                      supportTicket.timestamps?.updatedAt ||
                      supportTicket.timestamps?.lastResponseAt ||
                      supportTicket.timestamps?.createdAt
                    )}
                  </p>
                </div>
              </div>

              {/* Right Side - Button */}
              <div className="flex shrink-0 items-center">
                {/* View Details Button */}
                <button
                  onClick={() => onView(supportTicket)}
                  className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                  title="View Details"
                >
                  <FaEye className="mr-2" />
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SupportTicketList;
